import { useState } from "react";
import { Typography, Button, Flex, Row, Col, Grid } from "antd";
import cloudImage from '../assets/clouds.png';
// 新增 Context imports
import { useDemand } from '../context/DemandContext';
import { useFilesByIds } from '../context/FileManagementContext';
import { useVersionControl } from '../hooks/demand/useVersionControl';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface VideoSection {
  title: string;
  content: string;
  videos: Array<{
    id: string;
    name: string;
    url: string;
    version: string;
    versionIndex: number;
  }>;
}

// VideoCard 組件
const VideoCard = ({
  video,
  isShowCloud = true,
  selectedVersions,
  handleVersionSelect,
  getScriptForVersion,
  ceremonyType
}: {
  video: VideoSection,
  isShowCloud?: boolean,
  selectedVersions: { [key: string]: string },
  handleVersionSelect: (videoTitle: string, version: string) => void,
  getScriptForVersion: (ceremonyType: 'public' | 'family' | 'farewell', versionIndex: number) => string,
  ceremonyType: 'public' | 'family' | 'farewell'
}) => {
  // 根據選中的版本獲取當前影片
  const currentVideo = video.videos.find(v => v.version === selectedVersions[video.title]) || video.videos[0];
  
  // 根據選中的版本獲取對應的台詞內容
  const currentScript = currentVideo 
    ? getScriptForVersion(ceremonyType, currentVideo.versionIndex)
    : video.content;

  return (
    <Row className="bg-white">
      <Col xs={24} md={11} className="relative">
        <Flex vertical className="md:absolute md:top-[-50px] w-[100%]">
          <Flex vertical className="relative">
            {isShowCloud && (
              <img
                src={cloudImage}
                alt="cloud"
                className="absolute top-0 left-0 transform -translate-y-[50%] -translate-x-[3%]"
              />
            )}
            {currentVideo ? (
              <video
                controls
                key={currentVideo.id}
                className="w-full max-h-[400px] object-contain bg-[#3E3D3D] z-10"
              >
                <source src={currentVideo.url} type="video/mp4" />
                您的瀏覽器不支援影片播放。
              </video>
            ) : (
              <div className="flex items-center justify-center h-[300px] bg-gray-100 text-gray-500">
                暫無影片
              </div>
            )}
          </Flex>
          {video.videos.length > 0 && (
            <div className="py-2 bg-[#B0C0D7] overflow-x-auto">
              <Flex className="gap-4 px-4 min-w-max" justify="center">
                {video.videos.map((videoFile, vIndex) => (
                  <Button
                    key={vIndex}
                    type={selectedVersions[video.title] === videoFile.version ? 'primary' : 'default'}
                    onClick={() => handleVersionSelect(video.title, videoFile.version)}
                    style={{
                      background: selectedVersions[video.title] === videoFile.version
                        ? 'rgba(17, 56, 111, 0.55)'
                        : 'rgba(17, 56, 111, 0.55)',
                      borderColor: 'transparent',
                      color: '#FFFFFF',
                      borderRadius: 30,
                      padding: '4px 16px',
                      fontSize: 18,
                      fontWeight: 500,
                      flexShrink: 0
                    }}
                  >
                    {videoFile.version}
                  </Button>
                ))}
              </Flex>
            </div>
          )}
        </Flex>
      </Col>
      <Col xs={24} md={13}>
        <Flex vertical className="p-6 px-[60px] h-[500px] gap-4">
          <Title
            level={2}
            style={{
              margin: 0,
              fontSize: 36,
              fontWeight: 800,
              color: '#000',
              fontFamily: 'Noto Serif TC',
            }}
          >
            {video.title}
          </Title>
                  <Paragraph
          className="max-h-[400px] leading-[1.6] text-[20px] text-[#3E3D3D] whitespace-pre-line overflow-y-auto"
        >
          {currentScript}
        </Paragraph>
        </Flex>
      </Col>
    </Row>
  );
};

// 影片區 - 只顯示選中的影片版本
const VideoSection = () => {
  // 使用 Antd 的 useBreakpoint hook
  const screens = useBreakpoint();
  const isDesktop = screens.md; // md 斷點及以上為桌面版
  const isMobile = !screens.md; // md 斷點以下為移動版

  // 從 DemandContext 取得狀態
  const { state } = useDemand();
  
  // 從版本控制取得所有版本數據
  const { version, numToChinese } = useVersionControl();

  // 收集所有需要的檔案 ID（不調用 hooks）
  const collectAllVideoIds = () => {
    const allVideoIds: string[] = [];
    const videoMetadata: Array<{
      fileId: string;
      ceremonyType: 'public' | 'family' | 'farewell';
      versionIndex: number;
      version: string;
    }> = [];

    // 1. 處理歷史版本（從 sourceItem）- 只使用選中的影片
    if (version.sourceItem && version.sourceItem.length > 0) {
      version.sourceItem.forEach((versionData: any, versionIndex: number) => {
        ['public', 'family', 'farewell'].forEach((ceremonyType) => {
          const selectedVideos = versionData.ceremony?.[ceremonyType]?.files?.selected?.createdVideo || [];
          selectedVideos.forEach((v: any) => {
            if (v.fileId) {
              allVideoIds.push(v.fileId);
              videoMetadata.push({
                fileId: v.fileId,
                ceremonyType: ceremonyType as 'public' | 'family' | 'farewell',
                versionIndex: versionIndex,
                version: `版本${numToChinese(versionIndex + 1)}`
              });
            }
          });
        });
      });
    }

    // 2. 處理當前版本 - 只使用選中的影片
    ['public', 'family', 'farewell'].forEach((ceremonyType) => {
      const currentSelectedVideos = state.ceremony[ceremonyType as 'public' | 'family' | 'farewell'].files.selected.createdVideo || [];
      const nextVersionNumber = (version.sourceItem?.length || 0) + 1;
      
      currentSelectedVideos.forEach((v: any) => {
        if (v.fileId) {
          // 檢查是否已經在歷史版本中存在
          const existsInHistory = videoMetadata.some(meta => meta.fileId === v.fileId);
          if (!existsInHistory) {
            allVideoIds.push(v.fileId);
            videoMetadata.push({
              fileId: v.fileId,
              ceremonyType: ceremonyType as 'public' | 'family' | 'farewell',
              versionIndex: nextVersionNumber - 1,
              version: `版本${numToChinese(nextVersionNumber)}`
            });
          }
        }
      });
    });

    return { allVideoIds, videoMetadata };
  };

  // 收集所有檔案 ID 和元數據
  const { allVideoIds, videoMetadata } = collectAllVideoIds();
  
  // 一次性獲取所有檔案
  const allVideoFiles = useFilesByIds(allVideoIds);

  // 組織影片數據的純函數
  const organizeVideosByType = (ceremonyType: 'public' | 'family' | 'farewell'): Array<{
    id: string;
    name: string;
    url: string;
    version: string;
    versionIndex: number;
  }> => {
    const typeMetadata = videoMetadata.filter(meta => meta.ceremonyType === ceremonyType);
    
    return typeMetadata
      .map(meta => {
        const videoFile = allVideoFiles.find(file => file.id === meta.fileId);
        if (videoFile) {
          return {
            id: videoFile.id,
            name: videoFile.name,
            url: videoFile.url,
            version: meta.version,
            versionIndex: meta.versionIndex
          };
        }
        return null;
      })
      .filter((video): video is NonNullable<typeof video> => video !== null);
  };

  // 取得各個儀式的所有版本影片
  const publicVideos = organizeVideosByType('public');
  const familyVideos = organizeVideosByType('family');
  const farewellVideos = organizeVideosByType('farewell');

    // 獲取對應版本的台詞內容
  const getScriptForVersion = (ceremonyType: 'public' | 'family' | 'farewell', versionIndex: number) => {
    // 如果有歷史版本且索引有效，使用歷史版本的台詞
    if (version.sourceItem && version.sourceItem[versionIndex]) {
      return version.sourceItem[versionIndex].ceremony?.[ceremonyType]?.script || '暫無台詞內容';
    }
    // 否則使用當前版本的台詞
    return state.ceremony[ceremonyType].script || '暫無台詞內容';
  };

  // 建立影片區段資料
  const videoSections: VideoSection[] = [
    {
      title: '公祭影片',
      content: publicVideos.length > 0 
        ? getScriptForVersion('public', publicVideos[0].versionIndex)
        : state.ceremony.public.script || '暫無台詞內容',
      videos: publicVideos
    },
    {
      title: '家祭影片', 
      content: familyVideos.length > 0 
        ? getScriptForVersion('family', familyVideos[0].versionIndex)
        : state.ceremony.family.script || '暫無台詞內容',
      videos: familyVideos
    },
    {
      title: '遠行影片',
      content: farewellVideos.length > 0 
        ? getScriptForVersion('farewell', farewellVideos[0].versionIndex)
        : state.ceremony.farewell.script || '暫無台詞內容',
      videos: farewellVideos
    }
  ];

  // 初始化選中的版本
  const [selectedVersions, setSelectedVersions] = useState<{ [key: string]: string }>(() => {
    const initialVersions: { [key: string]: string } = {};
    videoSections.forEach(section => {
      if (section.videos.length > 0) {
        initialVersions[section.title] = section.videos[0].version;
      }
    });
    return initialVersions;
  });

  const handleVersionSelect = (videoTitle: string, version: string) => {
    setSelectedVersions(prev => ({
      ...prev,
      [videoTitle]: version
    }));
  };

  // 過濾出有影片的區段
  const availableVideoSections = videoSections.filter(section => section.videos.length > 0);

  // 如果沒有任何影片，顯示提示
  if (availableVideoSections.length === 0) {
    return (
      <div>
        {/* 影片區標題 */}
        <Flex vertical justify="center" align="center" className="py-[40px] pb-[70px]">
          <Title
            level={1}
            style={{
              fontSize: 52,
              fontWeight: 800,
              color: '#222834',
              margin: 0,
              fontFamily: 'Noto Serif TC',
            }}
          >
            影片區
          </Title>
          <Text className="text-[32px] text-[#11386F]"
            style={{
              fontFamily: 'Noto Serif TC',
            }}
          >
            Videos
          </Text>
        </Flex>

        <Flex justify="center" align="center" className="py-[100px]">
          <Text className="text-[20px] text-gray-500">
            暫無選中的影片，請先在需求單中選擇要展示的影片版本。
          </Text>
        </Flex>
      </div>
    );
  }

  return (
    <div>
      {/* 影片區標題 */}
      <Flex vertical justify="center" align="center" className="py-[40px] pb-[70px]">
        <Title
          level={1}
          style={{
            fontSize: 52,
            fontWeight: 800,
            color: '#222834',
            margin: 0,
            fontFamily: 'Noto Serif TC',
          }}
        >
          影片區
        </Title>
        <Text className="text-[32px] text-[#11386F]"
          style={{
            fontFamily: 'Noto Serif TC',
          }}
        >
          Videos
        </Text>
      </Flex>

      <Flex className="flex-col gap-[100px]">
        {availableVideoSections.map((video, index) => {
          const ceremonyTypes: ('public' | 'family' | 'farewell')[] = ['public', 'family', 'farewell'];
          return (
            <VideoCard
              key={index}
              video={video}
              isShowCloud={isMobile ? false : true}
              selectedVersions={selectedVersions}
              handleVersionSelect={handleVersionSelect}
              getScriptForVersion={getScriptForVersion}
              ceremonyType={ceremonyTypes[index]}
            />
          );
        })}
      </Flex>
    </div>
  );
};

export default VideoSection;